import {
  useImperativeHandle,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import Flag from "react-country-flag";
import {
  ARROW_DOWN_KEY,
  ARROW_UP_KEY,
  ENTER_KEY,
  ESCAPE_KEY,
  TAB_KEY,
} from "../../../core/constants/keyboard_keys";
import {
  getDropdownBodyHeight,
  getRootPopup,
} from "../../../core/helpers/dropdownHelper";
import { DropdownProps, useDropdownHooks } from "../../utils/dropdown-hook";
import DropdownBody from "./DropdownBody";
import DropdownItem, { IItemProp } from "./DropdownItem";
import { Icon } from "../Icon";
import PrimeTooltip from "../PrimeTooltip/PrimeTooltip";
import LoadingSpinner from "../Loading/LoadingSpinner";

import inputStyles from "../Inputs/Input.module.css";
import styles from "./Dropdown.module.css";

/** Instance value that is exposed to parent components when using ref */
export declare type DropdownActions = {
  open: () => void;
  close: () => void;
  toggle: () => void;
};

export const Dropdown = forwardRef<DropdownActions, DropdownProps>(
  (props: DropdownProps, ref) => {
    const {
      selectedItem,
      items,
      withoutCheckIcon,
      withFilter,
      setValue,
      onOpenStateChanged,
      title,
      placeholder,
      width,
      bodyWidth,
      className,
      popupClassName,
      buttonStyle,
      isOpen,
      setIsOpen,
      focusedIndex,
      setFocusedIndex,
      btnRef,
      container,
      openDropdown,
      closeDropdown,
      dropdownInPopup,
      invalid,
      validationError,
      disabled,
      maxVisible,
      id,
      innerButtonStyles,
      showTooltip,
      dataCy,
      isLoading,
      searchPlaceholder,
      ariaLable,
      required,
      withClearItem,
    } = useDropdownHooks(props);

    const [filteredItems, setFilteredItems] = useState<IItemProp[]>(items);

    // declare limited interface exposed to parent components when using ref
    useImperativeHandle<DropdownActions, DropdownActions>(ref, () => {
      const actions: DropdownActions = {
        open: function open() {
          openDropdown();
        },
        close: function close() {
          closeDropdown(false);
        },
        toggle: function toggle() {
          setIsOpen(!isOpen);
        },
      };
      return actions;
    });

    useEffect(() => onOpenStateChanged?.(isOpen), [isOpen, onOpenStateChanged]);

    const handleInputKeyDown = useCallback(
      (e: any) => {
        if (disabled) {
          return false;
        }
        const { key } = e;
        switch (key) {
          case ARROW_UP_KEY:
            // prevent scroll options popup
            e.preventDefault();
            setFocusedIndex((prev) => {
              if (prev.idx === 0 || prev.idx === -1)
                return { idx: filteredItems.length - 1, avoidScroll: false };
              return { idx: prev.idx - 1, avoidScroll: false };
            });
            break;
          case ARROW_DOWN_KEY:
            // prevent scroll options popup
            e.preventDefault();
            setFocusedIndex((p) => ({
              idx: (p.idx + 1) % filteredItems.length,
              avoidScroll: false,
            }));
            break;
          case ESCAPE_KEY:
            closeDropdown(true);
            break;
          case ENTER_KEY:
            if (isOpen) {
              // prevent reopen dropdown
              e.preventDefault();
              setValue(e, filteredItems[focusedIndex.idx], true);
            }
            break;
          case TAB_KEY:
            if (isOpen) closeDropdown(true);
            break;
          default:
            break;
        }
      },
      [
        focusedIndex,
        isOpen,
        setFocusedIndex,
        filteredItems,
        setIsOpen,
        openDropdown,
      ]
    );

    const selectedItemClasses = classNames({
      [styles.selectedDropdownItemText]: selectedItem?.icon,
      [styles.selectOption]: !selectedItem?.name,
      [styles.text]: selectedItem?.name,
    });

    const onFilter = (filter: string) => {
      const filtered = items.filter(
        (item) => item.name?.toLowerCase().indexOf(filter.toLowerCase()) > -1
      );
      setFilteredItems(filtered);
    };

    return (
      <div
        id={id}
        className={`${styles.dropdown} ${className}`}
        style={{ width }}
      >
        {title && (
          <label className={styles.dropdownTitle}>
            {required && <span className={inputStyles.requiredIcon}>*</span>}
            {title}
          </label>
        )}
        <button
          type="button"
          tabIndex={disabled ? -1 : 0}
          className={classNames({
            [styles.dropdownBtn]: true,
            [styles.inputValidationError]: invalid || validationError,
            [styles.disabled]: disabled,
          })}
          style={buttonStyle?.plate}
          disabled={disabled}
          aria-label={ariaLable}
          ref={btnRef}
          onClick={(e) => {
            if (e.type !== "click") return;
            e.preventDefault();
            if (!isOpen) {
              openDropdown();
            } else {
              closeDropdown();
            }
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            handleInputKeyDown(e);
          }}
          data-cy={dataCy}
        >
          <div
            className={styles.selectedDropdownItemName}
            style={innerButtonStyles}
          >
            {selectedItem?.flag && (
              <Flag
                svg
                countryCode={selectedItem.flag}
                className={styles.dropdownFlag}
              />
            )}
            {selectedItem?.icon && (
              <Icon name={selectedItem.icon} color={selectedItem.color} />
            )}
            {showTooltip ? (
              <>
                <span
                  id={id}
                  data-pr-tooltip={selectedItem?.name ?? placeholder}
                  className={selectedItemClasses}
                  style={buttonStyle?.text}
                >
                  {selectedItem?.name ?? placeholder}
                </span>
                <PrimeTooltip target={`#${id}`} />
              </>
            ) : (
              <span className={selectedItemClasses} style={buttonStyle?.text}>
                {selectedItem?.name ?? placeholder}
              </span>
            )}
            {isLoading && (
              <LoadingSpinner margin="0 0 0 auto" theme="primary" size={32} />
            )}
          </div>

          {isOpen ? (
            <Icon name="chevron-up" size={14} />
          ) : (
            <Icon name="chevron-down" size={14} />
          )}
        </button>
        {isOpen &&
          ReactDOM.createPortal(
            <DropdownBody
              dropdownInPopup={dropdownInPopup}
              innerRef={container}
              onFilter={onFilter}
              withFilter={withFilter}
              dropDownHeight={getDropdownBodyHeight(
                items.length,
                window.innerHeight <= 600 ? 6 : maxVisible,
                withFilter
              )}
              elem={btnRef.current}
              onClose={() => closeDropdown(true)}
              width={bodyWidth}
              searchPlaceholder={searchPlaceholder}
              withClearItem={withClearItem}
              keyHandler={handleInputKeyDown}
              className={popupClassName}
            >
              {filteredItems?.map((item: IItemProp, index) => {
                const focused = index === focusedIndex.idx;
                return (
                  <DropdownItem
                    item={item}
                    onSelectItem={(e) => setValue(item, e, true)}
                    key={item.id}
                    withoutCheckIcon={withoutCheckIcon}
                    focused={focused}
                    keyHandler={handleInputKeyDown}
                    onHover={() =>
                      setFocusedIndex((prev) => {
                        return prev.idx === index
                          ? prev
                          : { idx: index, avoidScroll: true };
                      })
                    }
                  />
                );
              })}
            </DropdownBody>,
            getRootPopup()
          )}
        {validationError ? (
          <div className={styles.validationError}>
            <Icon name="exclamation-circle" size={14} />
            {validationError}
          </div>
        ) : null}
      </div>
    );
  }
);

export default Dropdown;
